import React, { useState } from "react";
import { AlertTriangle, Check, XCircle } from "lucide-react";
import { cardFieldLabel, signedCardGaps } from "../lib/bulkConfirm";

function valueEntries(values) {
  return Object.entries(values || {}).filter(([, value]) => value || value === 0);
}

function isDateKey(key) {
  return key === "date" || String(key).endsWith("_date");
}

function changedRows(action) {
  const edits = action.edits || {};
  const values = action.values || {};
  return Object.entries(edits).filter(([key, value]) => String(values[key] ?? "") !== String(value ?? ""));
}

function seedDraft(action) {
  const values = action.values || {};
  const edits = action.edits && typeof action.edits === "object" ? action.edits : null;
  const editable = action.editable || null;
  const canEdit = (key) => !editable || editable.indexOf(key) !== -1;
  const keys = Array.isArray(editable) ? editable : Object.keys(edits || values);
  const next = {};
  keys.forEach((key) => {
    if (!canEdit(key)) return;
    const source = edits && Object.prototype.hasOwnProperty.call(edits, key) ? edits : values;
    const value = source[key];
    next[key] = value === undefined || value === null ? "" : String(value);
  });
  return next;
}

const ACCENT = {
  dark: {
    pending: ["border-l-amber-500", "bg-amber-600/20 text-amber-400", AlertTriangle],
    accepted: ["border-l-green-500", "bg-green-600/20 text-green-400", Check],
    skipped: ["border-l-slate-400", "bg-slate-600/30 text-slate-300", XCircle],
    error: ["border-l-red-500", "bg-red-600/20 text-red-400", AlertTriangle],
  },
  light: {
    pending: ["border-l-amber-600", "bg-amber-100 text-amber-800", AlertTriangle],
    accepted: ["border-l-green-700", "bg-green-100 text-green-800", Check],
    skipped: ["border-l-slate-500", "bg-slate-200 text-slate-700", XCircle],
    error: ["border-l-red-700", "bg-red-100 text-red-800", AlertTriangle],
  },
};

function stateKind(action) {
  if (action.duplicate || action.result === "possible_duplicate" || action.status === "error") return "error";
  if (action.status === "accepted") return "accepted";
  if (action.status === "skipped") return "skipped";
  return "pending";
}

function buttonClass(light, kind) {
  const base = "flex items-center gap-1.5 px-3 py-1.5 min-h-[44px] sm:min-h-0 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed";
  if (light) {
    return kind === "yes"
      ? `${base} bg-emerald-800 hover:bg-emerald-700 text-white`
      : `${base} bg-slate-800 hover:bg-slate-700 text-white`;
  }
  return kind === "yes"
    ? `${base} bg-emerald-600 hover:bg-emerald-500 text-white`
    : `${base} bg-slate-700 hover:bg-slate-600 text-slate-300`;
}

export default function AssistantConfirmCard({ action, busy, onConfirm, onCancel, onSave, tone = "dark" }) {
  const [editing, setEditing] = useState(false);
  const editable = action.editable || null;
  const canEdit = (key) => !editable || editable.indexOf(key) !== -1;
  const [draft, setDraft] = useState(() => seedDraft(action));
  const duplicate = action.result === "possible_duplicate";
  const info = Boolean(action.info);
  const match = action.possibleDuplicate || {};
  const disabled = Boolean(busy);
  const gaps = signedCardGaps(action);
  const saved = action.edits && typeof action.edits === "object";
  const light = tone === "light";
  const accent = ACCENT[light ? "light" : "dark"][stateKind(action)];
  const Icon = accent[2];
  const yesBtn = buttonClass(light, "yes");
  const noBtn = buttonClass(light, "no");
  const bodyTone = light
    ? "bg-white border border-slate-200 text-slate-800"
    : "bg-amber-950/30 border border-amber-800/40 text-slate-300";
  const note = light ? "text-red-800" : "text-amber-200";
  return (
    <div className="flex gap-2" data-testid="agent-confirm-card" data-action-id={action.actionId}>
      <div className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center mt-0.5 ${accent[1]}`} data-testid="agent-confirm-icon">
        <Icon size={14} />
      </div>
      <div className="flex flex-col max-w-[85%] items-start">
      <div data-testid="agent-confirm-body" className={`rounded-2xl px-3.5 py-2.5 border-l-4 ${accent[0]} ${bodyTone} rounded-tl-sm`}>
        <p className={`text-[11px] font-medium uppercase tracking-wider mb-1 ${light ? "text-slate-800" : "text-amber-400"}`}>Confirm Action</p>
        <p className="text-[13px] leading-relaxed">{action.description}</p>
        {action.filename ? <p className="text-[13px] mt-1">{`From ${action.filename}`}</p> : null}
        {action.duplicate ? <p className={`text-[13px] mt-1 ${note}`}>{action.duplicate}</p> : null}
        {gaps.map((label) => (
          <p key={label} className="text-[13px]" data-testid="card-needs">{`Needs: ${label}`}</p>
        ))}
        {!editing && saved ? (
          <div data-testid="card-changed">
            <p className="text-[13px] font-medium">Changed</p>
            {changedRows(action).map(([key, value]) => (
              <p key={key} className="text-[13px]">{`${cardFieldLabel(key)}: ${value}`}</p>
            ))}
          </div>
        ) : null}
        {!saved && valueEntries(action.values).filter(([key]) => !editing || !canEdit(key)).map(([key, value]) => (
          <p key={key} className="text-[13px]">{`${cardFieldLabel(key)}: ${value}`}</p>
        ))}
        {editing && Object.keys(draft).map((key) => (
          <label key={key} className="block text-[12px] mt-1">
            {cardFieldLabel(key)}
            <input
              data-testid={`edit-field-${action.actionId}-${key}`}
              type={isDateKey(key) ? "date" : "text"}
              value={draft[key]}
              onChange={(event) => setDraft((prev) => ({ ...prev, [key]: event.target.value }))}
              className="mt-0.5 w-full text-sm text-slate-800 rounded px-2 py-1"
            />
          </label>
        ))}
        {duplicate ? (
          <>
            <p className={`text-[13px] leading-relaxed mt-2 ${note}`}>
              {`Possible duplicate: ${match.title || "Untitled"}, created ${match.created_at || ""}, ${match.status || ""}.`}
            </p>
            <div className="flex flex-wrap gap-2 mt-2.5">
              <button type="button" disabled={disabled} onClick={() => onConfirm(action.actionId, "add_anyway")} className={yesBtn}><Check size={12} />Add anyway</button>
              <button type="button" disabled={disabled} onClick={() => onConfirm(action.actionId, "use_existing")} className={noBtn}>Use existing</button>
              <button type="button" disabled={disabled} onClick={() => onCancel(action.actionId)} className={noBtn}><XCircle size={12} />Cancel</button>
            </div>
          </>
        ) : info && action.status === "pending" ? (
          <div className="flex flex-wrap gap-2 mt-2.5">
            <button type="button" disabled={disabled} onClick={() => onCancel(action.actionId)} className={noBtn}>Dismiss</button>
          </div>
        ) : action.status === "pending" ? (
          <div className="flex flex-wrap gap-2 mt-2.5">
            {onSave && editing ? (
              <button type="button" onClick={() => { onSave(action.actionId, draft); setEditing(false); }} className={yesBtn}>Save</button>
            ) : null}
            {onSave && !editing ? (
              <button type="button" disabled={disabled} onClick={() => setEditing(true)} className={noBtn}>Edit</button>
            ) : null}
            <button type="button" disabled={disabled || editing || gaps.length > 0} onClick={() => onConfirm(action.actionId)} className={yesBtn}><Check size={12} />Confirm</button>
            <button type="button" disabled={disabled} onClick={() => onCancel(action.actionId)} className={noBtn}><XCircle size={12} />Cancel</button>
          </div>
        ) : (
          <p className="text-xs mt-2">{action.status === "accepted" ? "Accepted" : "Skipped"}</p>
        )}
      </div>
      </div>
    </div>
  );
}
